import { Component, OnInit, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { CurrentAgentStatesModuleDataObj, WVBase, WVMain } from '../wallboard-view.model';
import { CurrentAgentStatesSettingTransferObj, ModuleType } from '../../wallboards.model';

@Component({
    selector: 'cv-wallboard-view-current-agent-states',
    templateUrl: './wallboard-view-current-agent-states.component.html',
    styleUrls: ['./wallboard-view-current-agent-states.component.scss']
})
export class WallboardViewCurrentAgentStatesComponent implements OnInit, OnDestroy {
    @Input() firstSlide: boolean;
  
    public wvf: WVMain;
    public wvcasf: WVBase;
    public currentAgentStatesState: CurrentAgentStatesSettingTransferObj;
    public currentAgentStatesData: CurrentAgentStatesModuleDataObj;
    filledOnCall: CurrentAgentStatesModuleDataObj[] = [];
    filledUnavailable: CurrentAgentStatesModuleDataObj[] = [];
    filledAvailable: CurrentAgentStatesModuleDataObj[] = [];

    private dataUpdated: boolean = false;
    public tableHeight: number = 0;

    private updateTimerRef:NodeJS.Timeout = null;
    private isDestroyed: boolean = false;

    public paddedData = [[], [], []];

    get hasBackdrop(): boolean {
        return this.wvcasf?.Slide.HasBackdrop ?? false;
    }

    get moduleKey(): string {
        return this.wvcasf?.Slide.WallboardModuleDetails[0].WallboardModuleKey ?? "";
    }

    get backgroundID(): number {
        return this.wvcasf?.Slide.BackgroundImageID ?? 0;
    }

    constructor(
        private service: WallboardViewService,
        private cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.wvcasf = this.service.WVAgentStates;

        this.wvf.RunSlide = this.firstSlide;
        if (!this.firstSlide)
            this.wvf.NextSlideReady = false;

        let moduleState = this.service.loadModuleState(ModuleType.CurrentAgentStates, this.wvf.WallboardCreatorID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.currentAgentStatesState = response.currentAgentStatesObj;
        });

        let currentAgentStatesModuleData = this.service.getCurrentAgentStatesModuleData(this.wvcasf.Slide.WallboardID, this.moduleKey).then(response => {
            if (this.isDestroyed)
                return;

            this.currentAgentStatesData = response;
        });

        this.updateFilledColumns();

        Promise.all([moduleState, currentAgentStatesModuleData]).then(responses => {
            if (this.firstSlide)
                this.wvf.FirstSlideReady = true;
            else
                this.wvf.NextSlideReady = true;

            let $viewSlide = angular.element(document).find('.wallboardViewSlide');
            $viewSlide.height('100vh');
            this.tableHeight = $viewSlide.height() - 132;

            this.runSlide();
        });
    }

    ngOnDestroy(): void {
        // End data updates
        if (this.updateTimerRef != null)
            clearInterval(this.updateTimerRef);

        this.isDestroyed = true;
    }

    updateFilledColumns(): void {
        this.filledOnCall = this.fillColumn(0, this.currentAgentStatesData?.OnCall || [], ['callDirection', 'groupName', 'stateName', 'stateTime']);
        this.filledUnavailable = this.fillColumn(1, this.currentAgentStatesData?.Unavailable || [], ['groupName', 'stateName', 'stateTime', 'isACW']);
        this.filledAvailable = this.fillColumn(2, this.currentAgentStatesData?.Available || [], ['groupName', 'stateTime']);
    }

    getLimitTo(index) {
        let conHeight = $($(".wvCurrentAgentStatesColumn")[index]).height() - $($(".wvCurrentAgentStatesColumnTitle")[index]).outerHeight();
        let rowHeight = $($(".wvCurrentAgentStatesGridCell")[index]).outerHeight();

        if (conHeight < rowHeight)
            conHeight = $($(".wvCurrentAgentStatesColumn")[index]).height() - rowHeight;

        let rows = conHeight / rowHeight;
        return rows % 1 < 0.75 ? Math.floor(rows) - 1 : Math.floor(rows);
    }

    /**
     * Used to fill the ng-repeats with blank data so the css grid doesn't center them vertically when there aren't enough rows.
     * @param index Index of box to find max size for; 0 for OnCall, 1 for Unavailable, 2 for Available
     * @param data Data that is being repeated over
     * @param includedProperties List of properties to include in the blank data. All of these will be set to empty string ("")
     */
    fillColumn(index: number, data: CurrentAgentStatesModuleDataObj[], includedProperties: string[]): CurrentAgentStatesModuleDataObj[] {
        if (!data || !this.currentAgentStatesData)
            return [];

        let maxDataRows = this.getLimitTo(index);
        if (isNaN(maxDataRows) || maxDataRows === Infinity)
            maxDataRows = 10;

        let paddedData = angular.copy(data);

        let padding: CurrentAgentStatesModuleDataObj = {
            stateName:"",
            stateTime:"",
            callDirection:"",
            groupName:"",
            isACW:"",
            OnCall: [],
            Unavailable: [],
            Available: [],
            InboundCount: 0,
            OutboundCount: 0,
            UnavailableCount: 0,
            AvailableCount: 0,
            TotalCount: 0,
            CallsInQueueCount: 0,
            LongestCallInQueue: null,
            LongestCallInQueueFormatted: "",
            AgentStateCategories: new Map<string, string>()
        };

        while (paddedData.length < maxDataRows) {
            
            for (let i = 0; i < includedProperties.length; i++) {
                padding[includedProperties[i]] = "";
            }

            paddedData.push(padding);
        }

        return paddedData.slice(0, maxDataRows);
    }

    async runSlide() {
        if (!this.dataUpdated) {
            // Reload the data every 5 secs.
            this.updateTimerRef = setInterval(() => {
                this.updateSlideData();

                this.updateFilledColumns();
            }, WallboardViewService.REALTIME_DATA_REFRESH_INTERVAL);

            this.dataUpdated = true;
        }

        if (this.wvf.RunSlide) {
            setTimeout(() => {
                // Was originally this.currentAgentStatesData.length, but that makes no sense because this.currentAgentStatesData is not an array or other collection.
                // This is the closest thing that even approximates something coherent.
                for (let i = 0; i < this.currentAgentStatesData?.OnCall.length; i++) {
                    this.animateRow(0);
                }
                this.cdr.detectChanges();
            }, 0);
        }
        else {
            setTimeout(() => {
                this.runSlide();
            }, 1000);
        }
    }

    animateRow(idx) {
        setTimeout(() => {
            $(".wallboardViewSlide[mid=" + this.moduleKey + "]:last").find("#row" + idx).fadeIn(1000);
        }, 500 * idx);
    }

    async updateSlideData() {
        let response = await this.service.getCurrentAgentStatesModuleData(this.wvcasf.Slide.WallboardID, this.moduleKey);
        this.currentAgentStatesData = response;
    }
}
